import { Pause, Play, RefreshCw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useBulkUpload } from "@/contexts/uploads/BulkUploadContext";
import BulkUploadFileItem from "./BulkUploadFileItem";

const BulkUploadProgress = () => {
  const {
    state,
    progress,
    isPanelOpen,
    closePanel,
    pauseUpload,
    resumeUpload,
    retryFailed,
    clearQueue,
    clearCompleted,
    removeFile,
    startUpload,
  } = useBulkUpload();

  const selectedAgentName = state.agentName;

  const hasPending = progress.pending > 0;
  const hasFailed = progress.failed > 0;
  const hasCompleted = progress.completed > 0;
  const isProcessing = state.isProcessing && !state.isPaused;

  return (
    <Sheet open={isPanelOpen} onOpenChange={closePanel}>
      <SheetContent side="right" showClose={false} className="w-full sm:max-w-xl flex flex-col h-full p-0">
        {/* Header */}
        <div className="flex items-center justify-between border-b p-6 sm:p-8">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Bulk Upload</h2>
            <p className="text-sm text-gray-500 mt-1">
              {progress.completed} of {progress.total} files processed
            </p>
            {selectedAgentName && (
              <p className="text-xs text-gray-600 mt-2">
                Documents will be attached to <span className="font-semibold text-genie-primary">{selectedAgentName}</span> genie
              </p>
            )}
          </div>
          <button onClick={closePanel} className="text-gray-400 hover:text-gray-600 text-2xl font-light transition-colors">
            &times;
          </button>
        </div>

        {/* Overall Progress Bar */}
        {progress.total > 0 && (
          <div className="px-6 sm:px-8 py-4 border-b">
            <div className="flex justify-between text-sm mb-2">
              <span className="font-medium text-gray-700">Overall Progress</span>
              <span className="text-gray-500">{progress.overallProgress}%</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-genie-primary to-genie-secondary transition-all duration-300"
                style={{ width: `${progress.overallProgress}%` }}
              />
            </div>
            <div className="flex gap-4 mt-2 text-xs text-gray-500">
              {progress.inProgress > 0 && <span className="text-genie-primary">{progress.inProgress} in progress</span>}
              {progress.pending > 0 && <span>{progress.pending} pending</span>}
              {progress.completed > 0 && <span className="text-green-600">{progress.completed} completed</span>}
              {progress.failed > 0 && <span className="text-red-600">{progress.failed} failed</span>}
            </div>
          </div>
        )}

        {/* File List */}
        <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-4 space-y-2">
          {state.files.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p>No files in queue</p>
              <p className="text-sm mt-1">Add files from the Bulk Upload tab to get started</p>
            </div>
          ) : (
            state.files.map((file) => <BulkUploadFileItem key={file.id} file={file} onRemove={removeFile} showRemove={!isProcessing} />)
          )}
        </div>

        {/* Footer Actions */}
        {state.files.length > 0 && (
          <div className="border-t px-6 sm:px-8 py-4 flex flex-wrap gap-3 bg-white">
            {/* Start button - show when not started yet */}
            {!state.isProcessing && hasPending && (
              <Button
                onClick={startUpload}
                className="bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold px-6 shadow-lg shadow-genie-primary/30 hover:shadow-xl hover:shadow-genie-primary/40 hover:scale-105 transition-all duration-200"
              >
                <Play className="h-4 w-4 mr-2" />
                Start Upload
              </Button>
            )}

            {/* Pause/Resume - show when processing */}
            {state.isProcessing && (
              <Button variant="outline" onClick={state.isPaused ? resumeUpload : pauseUpload}>
                {state.isPaused ? (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Resume
                  </>
                ) : (
                  <>
                    <Pause className="h-4 w-4 mr-2" />
                    Pause
                  </>
                )}
              </Button>
            )}

            {/* Retry Failed */}
            {hasFailed && !isProcessing && (
              <Button variant="outline" onClick={retryFailed}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry Failed
              </Button>
            )}

            {/* Clear Completed */}
            {hasCompleted && !isProcessing && (
              <Button variant="outline" onClick={clearCompleted}>
                Clear Completed
              </Button>
            )}

            {/* Clear All - only when not processing */}
            {!isProcessing && (
              <Button variant="outline" onClick={clearQueue} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                <Trash2 className="h-4 w-4 mr-2" />
                Clear All
              </Button>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default BulkUploadProgress;
